import { createDocument, getAllData } from "apis/customApiHandler";
import Spinner from "components/Spinner";
import { Input, Select } from "styled";
import { Fragment, useCallback, useState } from "react";
import { BiCloudDownload } from "react-icons/bi";
import { useQuery } from "react-query";
import ReactSelect from "react-select";
import { filterSelectStyles } from "utils/reactSelectStyles";
import { TD, TH } from "styled/table";
import exportExcel from "utils/exportExcel";
import Modal from "components/Modal";
import { Button } from "styled";
import { getFormattedDate, formatDate, formatMonthYear } from "utils/helpers";
import { formatDateToYYYYMM } from "utils/helpers";
import * as XLSX from "xlsx";

const CPKMTrendReport = () => {
  const [selectedFleets, setSelectedFleets] = useState([]);
  const [contractType, setContractType] = useState("All");
  const [dateFrom, setDateFrom] = useState(
    getFormattedDate(new Date(new Date().getTime() - 365 * 24 * 60 * 60 * 1000))
  );
  const [dateTo, setDateTo] = useState(getFormattedDate());
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const monthMap = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  const getData = async () => {
    setIsLoading(true);
    try {
      const formattedFrom = dateFrom ? dateFrom.slice(0, 7) : "";
      const formattedTo = dateTo ? dateTo.slice(0, 7) : "";

      if (!formattedFrom || !formattedTo) {
        throw new Error("Please select Start Date and End Date");
      }

      const dt = await createDocument("/cpkm/report/", {
        ...(selectedFleets?.includes("all")
          ? {
              fleets: getFleetOptions()
                .filter((item) => item.value !== "all" && item.value !== "clear")
                .map((item) => item.value)
                .join(","),
            }
          : selectedFleets.length && {
              fleets: selectedFleets.map((fleet) => fleet).join(","),
            }),
        ...(formattedFrom && {
          from: formattedFrom,
        }),
        ...(formattedTo && {
          to: formattedTo,
        }),
        // Keep same logic (explicitly requesting CPKM)
        contractType: "CPKM",
      });
      setData(dt);
    } catch (err) {
      if (err.message === "Please select from date") {
        alert("Please select from date");
      } else {
        console.error(err);
        alert(err.message || "An error occurred while fetching data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // (Kept as-is; you can still download if needed)
  const getDownloadData = async () => {
    setDownloading(true);
    try {
      const formattedFrom = dateFrom ? dateFrom.slice(0, 7) : "";
      const formattedTo = dateTo ? dateTo.slice(0, 7) : "";

      const dt = await createDocument("/cpkm/report/", {
        ...(selectedFleets?.includes("all")
          ? {
              fleets: getFleetOptions()
                .filter((item) => item.value !== "all" && item.value !== "clear")
                .map((item) => item.value)
                .join(","),
            }
          : selectedFleets.length && {
              fleets: selectedFleets.map((fleet) => fleet).join(","),
            }),
        ...(formattedFrom && { from: formattedFrom }),
        ...(formattedTo && { to: formattedTo }),
        ...(contractType !== "" && { contractType }),
      });

      if (dt.data) {
        const monthNames = dt.months?.map((month) => {
          const [year, monthNum] = month.split("-");
          return `${monthMap[parseInt(monthNum, 10)]} ${year}`;
        });

        const exportData = dt.data
          ?.map((entry, idx) => {
            let monthWise = {};
            dt.months.forEach((month, monthIdx) => {
              const monthKey = monthNames[monthIdx];
              const monthData = entry.monthWiseBilledCPKM.find(
                (item) => item.monthYear === month
              );
              const monthWiseBilledCPKMForFleet = monthData?.billedCPKM || 0;
              const vehicleCount = monthData?.vehicleCount || 0;
              
              // Create paired columns: Vehicle and KM for each month
              monthWise[`${monthKey} - Vehicles`] = vehicleCount;
              monthWise[`${monthKey} - KM`] = monthWiseBilledCPKMForFleet;
            });
            const result = {
              "S.N": idx + 1,
              "Fleet Name": `${entry.fleetName} (${entry.fleetCity})`,
              ...monthWise,
              Total: entry.total,
              Average: entry.average,
            };

            return result;
          });

        let totalRow = { "S.N": "--", "Fleet Name": "TOTAL" };
        let overallTotal = 0,
          totalCount = 0;

        dt.months.forEach((month, monthIdx) => {
          const monthNamesEntry = `${monthMap[parseInt(month.split("-")[1], 10)]} ${
            month.split("-")[0]
          }`;
          const monthData = dt.totalForMonths.find((t) => t.monthYear === month);
          const monthTotal = monthData?.monthWiseBilledCPKM ?? 0;
          const vehicleCount = monthData?.vehicleCount ?? 0;

          // Paired columns for total row as well
          totalRow[`${monthNamesEntry} - Vehicles`] = vehicleCount;
          totalRow[`${monthNamesEntry} - KM`] = monthTotal;
          
          overallTotal += monthTotal;

          if (monthTotal !== 0) {
            totalCount++;
          }
        });

        totalRow["Total"] = overallTotal;
        totalRow["Average"] = parseInt(overallTotal / (totalCount ?? 1), 10);

        exportData.push(totalRow);

        const ws = XLSX.utils.json_to_sheet(exportData);
        const merge = [
          { s: { r: exportData.length, c: 0 }, e: { r: exportData.length, c: 1 } },
        ];
        if (!ws["!merges"]) ws["!merges"] = [];
        ws["!merges"] = ws["!merges"].concat(merge);
        ws["A" + (exportData.length + 1)] = { t: "s", v: "TOTAL" };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CPKM Trend Report");

        XLSX.writeFile(wb, "cpkm_trend.xlsx");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData("/customer-fleet", {
      limit: 3000,
    })
  );

  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Clear",
          value: "clear",
        },
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
          };
        }),
      ];
    }
  }, [allFleets]);

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value;
    setDateFrom(selectedDate);

    const oneYearAheadDate = new Date(selectedDate);
    oneYearAheadDate.setFullYear(oneYearAheadDate.getFullYear() + 1);
    oneYearAheadDate.setDate(oneYearAheadDate.getDate() - 1);

    const currentDate = new Date();

    const finalDate = oneYearAheadDate > currentDate ? currentDate : oneYearAheadDate;
    setDateTo(getFormattedDate(finalDate));
};
  const handleToDateChange = (e) => {
    const selectedDate = e.target.value;
    setDateTo(selectedDate);

    // Calculate the current date range
    const fromDate = new Date(dateFrom);
    const toDate = new Date(selectedDate);
    
    // Calculate difference in days
    const diffTime = Math.abs(toDate - fromDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // If current range is greater than 1 year (365 days), auto-adjust start date
    if (diffDays > 365) {
      const oneYearBeforeDate = new Date(selectedDate);
      oneYearBeforeDate.setFullYear(oneYearBeforeDate.getFullYear() - 1);
      oneYearBeforeDate.setDate(oneYearBeforeDate.getDate() + 1);

      setDateFrom(getFormattedDate(oneYearBeforeDate));
    }
  };

  return (
    <>
      <div>
        <div className="w-full px-8 flex flex-wrap lg:flex-nowrap justify-start items-end gap-4">
          <label className="w-full py-3 flex flex-col gap-2">
            Select Fleets
            <ReactSelect
              styles={filterSelectStyles}
              className="shadow-md rounded-md"
              options={getFleetOptions()}
              value={
                selectedFleets &&
                selectedFleets?.map((fleet) => {
                  return {
                    value: fleet,
                    label: getFleetOptions()?.find((item) => item?.value === fleet)?.label,
                  };
                })
              }
              onChange={(value) => {
                let allValues = value?.map((item) => item.value);
                if (allValues?.includes("clear")) {
                  setSelectedFleets([]);
                  return;
                }
                if (!selectedFleets?.includes("all") && allValues.includes("all")) {
                  setSelectedFleets(["all"]);
                  return;
                }
                if (selectedFleets.includes("all")) {
                  value = value.filter((item) => item.value !== "all");
                }
                setSelectedFleets(value.map((fleet) => fleet.value));
              }}
              name="fleets"
              isMulti={true}
            />
          </label>
          <label className="w-full py-3 flex flex-col gap-2">
            Start Date{" "}
            <Input
              type="date"
              max={getFormattedDate()}
              name="dateFrom"
              value={dateFrom}
              onChange={handleFromDateChange}
            />
          </label>
          <label className="w-full py-3 flex flex-col gap-2">
            End Date{" "}
            <Input
              type="date"
              min={dateFrom || undefined}
              max={getFormattedDate()}
              name="dateTo"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <div className="flex items-end gap-4 mb-3">
            <Button
              type="button"
              disabled={selectedFleets.length === 0 || !dateFrom || !dateTo}
              bgPrimary
              onClick={() => getData()}
            >
              Generate Report
            </Button>

            {downloading ? (
              "Downloading..."
            ) : (
              selectedFleets.length !== 0 && (
                <BiCloudDownload
                  className="w-10 h-10 text-green-500 hover:scale-105 cursor-pointer text-center"
                  onClick={() => {
                    getDownloadData();
                  }}
                />
              )
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col m-2 lg:m-10">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                 <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                   <thead className="bg-gray-50">
                     <tr>
                       <TH scope="col" rowSpan={2} className="border border-gray-300 !text-center !font-bold">
                         S.N
                       </TH>
                       <TH scope="col" rowSpan={2} className="border border-gray-300 !text-center !font-bold">
                         Fleet Name
                       </TH>
                       {data.months?.map((month) => {
                         const [y, m] = month.split("-");
                         return (
                           <TH key={month} scope="col" colSpan={2} className="border border-gray-300 !text-center !font-bold">
                             {monthMap[parseInt(m, 10)]} {y}
                           </TH>
                         );
                       })}
                       <TH scope="col" rowSpan={2} className="border border-gray-300 !text-center !font-bold">
                         Total
                       </TH>
                       <TH scope="col" rowSpan={2} className="border border-gray-300 !text-center !font-bold">
                         Average
                       </TH>
                     </tr>
                     <tr>
                       {data.months?.map((month) => (
                         <Fragment key={`${month}-sub`}>
                           <TH scope="col" className="border border-gray-300 !text-center text-sm font-medium">
                             Vehicle
                           </TH>
                           <TH scope="col" className="border border-gray-300 !text-center text-sm font-medium">
                             KM
                           </TH>
                         </Fragment>
                       ))}
                     </tr>
                   </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {data?.data &&
                      data?.data?.map((item, idx) => (
                        <ViewList
                          key={item.fleetId || `${item.fleetName}-${idx}`}
                          idx={idx}
                          data={item}
                          months={data.months}
                          dateFrom={dateFrom}
                          dateTo={dateTo}
                        />
                      ))}

                     {(() => {
                       let totalSum = 0;
                       let nonZeroCount = 0;
                       return (
                         <tr className="text-gray-700 font-semibold bg-green-100">
                           <TD className="border border-gray-300 text-center font-semibold">
                             --
                           </TD>
                           <TD className="border border-gray-300 text-center font-semibold">
                             TOTAL
                           </TD>

                           {data.months?.map((month, i) => {
                             const totalEntry = data.totalForMonths.find(
                               (t) => t.monthYear === month
                             );
                             const veh = totalEntry?.vehicleCount ?? 0;
                             const km = totalEntry?.monthWiseBilledCPKM ?? 0;

                             if (typeof km === "number" && km > 0) {
                               nonZeroCount += 1;
                               totalSum += km;
                             }

                             return (
                               <Fragment key={`tot-${month}-${i}`}>
                                 <TD className="text-center border border-gray-300">{veh}</TD>
                                 <TD className="text-center border border-gray-300">{km || 0}</TD>
                               </Fragment>
                             );
                           })}

                           <TD className="text-center border border-gray-300 font-semibold">{totalSum}</TD>
                           <TD className="text-center border border-gray-300 font-semibold">
                             {nonZeroCount > 0
                               ? parseInt(totalSum / nonZeroCount, 10)
                               : "--"}
                           </TD>
                         </tr>
                       );
                     })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CPKMTrendReport;

/* ===== Row component with paired month cells (Vehicle, KM) and modal on KM click ===== */
const ViewList = ({ idx, data, months, dateFrom, dateTo }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [fleet, setFleet] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modalQueryProp, setModalQueryProp] = useState(null);

  const getModalData = async (modalQueryProp) => {
    setShowModal(true);
    if (modalQueryProp) {
      setIsLoading(true);
      const dt = await createDocument("/cpkm/report/modalInfo", {
        fleets: modalQueryProp?.fleetId,
        contractType: modalQueryProp?.contractType,
        selectedMonthYear: modalQueryProp?.selectedMonthYear,
        from: modalQueryProp?.from,
        to: modalQueryProp?.to,
      });
      setModalData(dt);
      setModalQueryProp(modalQueryProp);
      setIsLoading(false);
    }
  };

  const getModalRecords = () => {
    if (!modalData?.data?.[0]) return [];
    if (modalData.data[0].billedData && modalData.data[0].billedData.length > 0) {
      return modalData.data[0].billedData[0].data || [];
    }
    if (modalData.data[0].totalData) {
      return modalData.data[0].totalData;
    }
    return [];
  };

  return (
    <Fragment>
      {showModal && (
        <Modal
          handleClick={() => setShowModal(false)}
          title={"CPKM Trend Report"}
          className="max-w-2xl"
        >
          {isLoading ? (
            <div className="mb-6">
              <Spinner />
            </div>
          ) : (
            <>
              <h1 className="w-full flex justify-end text-end px-2 my-1">
                <Button
                  onClick={() => {
                    exportExcel(
                      modalData?.data[0]?.totalData.length > 0 &&
                        modalData?.data[0]?.totalData.map((modal, idx) => ({
                          "S.N": idx + 1,
                          "Fleet Name": `${fleet} (${data?.fleetCity || "-"})`,
                          "Hub Name": modal.hubName,
                          "Vehicle Registration Number": modal.registrationNumber,
                          MonthYear: formatMonthYear(modal.monthYear),
                          "Billed CPKM": modal?.billedCPKM,
                          "Uploaded By": modal?.uploadedBy,
                          "Date of Upload": formatDate(modal.dateOfUpload),
                        })),
                      "CPKM_Trend_Report_Data"
                    );
                  }}
                  className=" text-blue-500 hover:scale-105 cursor-pointer text-right"
                >
                  Download
                </Button>
              </h1>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <TH scope="col">S.N.</TH>
                    <TH scope="col">Fleet Name</TH>
                    <TH scope="col">Hub Name</TH>
                    <TH scope="col">Vehicle Registration Number</TH>
                    <TH scope="col">Month Year</TH>
                    <TH scope="col">Billed CPKM</TH>
                    <TH scope="col">Uploaded By</TH>
                    <TH scope="col">Date of Upload</TH>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getModalRecords().length > 0 &&
                    getModalRecords().map((modal, index) => (
                      <tr key={index}>
                        <TD>{index + 1}</TD>
                        <TD>{`${fleet} (${data?.fleetCity || "-"})`}</TD>
                        <TD>{modal.hubName}</TD>
                        <TD>{modal.registrationNumber}</TD>
                        <TD>{formatMonthYear(modal.monthYear)}</TD>
                        <TD>{modal.billedCPKM}</TD>
                        <TD>{modal.uploadedBy}</TD>
                        <TD>{formatDate(modal.dateOfUpload)}</TD>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </Modal>
      )}

       <tr className={`${idx % 2 !== 0 && "bg-gray-50"}`}>
         <TD className="border border-gray-300 text-center">{idx + 1}</TD>
         <TD className="border border-gray-300 text-left px-2">{`${data.fleetName} (${data.fleetCity || "-"})`}</TD>

         {months.map((month, monthIdx) => {
           const monthEntry = data.monthWiseBilledCPKM.find(
             (item) => item.monthYear === month
           );
           const veh = monthEntry?.vehicleCount || 0;
           const km = monthEntry?.billedCPKM || 0;

           const prevMonthEntry =
             monthIdx > 0
               ? data.monthWiseBilledCPKM.find(
                   (item) => item.monthYear === months[monthIdx - 1]
                 )
               : null;

           const prevVeh = prevMonthEntry?.vehicleCount ?? null;
           const prevKm = prevMonthEntry?.billedCPKM ?? null;

           const vehDown = prevVeh !== null && veh < prevVeh;

           const kmDown = prevKm !== null && km < prevKm;

           const vehClass = vehDown ? "text-red-500 font-medium" : "";

           const kmClass = kmDown
             ? "text-red-500 font-medium"
             : "";
           
           // Add cursor pointer only if KM value is greater than 0
           const kmCursorClass = km && km > 0 ? "cursor-pointer" : "";

           return (
             <Fragment key={`${data.fleetId || data.fleetName}-${month}`}>
               <TD className={`text-center border border-gray-300 ${vehClass} ${veh>0 ? "cursor-pointer" : ""}`}
               onClick={() => {
                if (veh && veh > 0) {
                getModalData({
                  fleetId: data.fleetId || data.fleetName,
                  contractType: "CPKM",
                  selectedMonthYear: month,
                  from: dateFrom ? dateFrom.slice(0, 7) : undefined,
                  to: dateTo ? dateTo.slice(0, 7) : undefined,
                });
                setFleet(data.fleetName);
              }}
            }
               >
                 {veh}
               </TD>

               <TD
                 className={`text-center border border-gray-300 ${kmClass} ${kmCursorClass}`}
                 onClick={() => {
                   // Only open modal if KM value is greater than 0
                   if (km && km > 0) {
                     getModalData({
                       fleetId: data.fleetId || data.fleetName,
                       contractType: "CPKM",
                       selectedMonthYear: month,
                       from: dateFrom ? dateFrom.slice(0, 7) : undefined,
                       to: dateTo ? dateTo.slice(0, 7) : undefined,
                     });
                     setFleet(data.fleetName);
                   }
                 }}
                 title={km && km > 0 ? "View vehicles" : ""}
               >
                 {km || "--"}
               </TD>
             </Fragment>
           );
         })}

         <TD
           className="text-center border border-gray-300 cursor-pointer hover:text-green-500"
           onClick={() => {
             getModalData({
               fleetId: data.fleetId || data.fleetName,
               contractType: "CPKM",
               from: dateFrom ? dateFrom.slice(0, 7) : undefined,
               to: dateTo ? dateTo.slice(0, 7) : undefined,
             });
             setFleet(data.fleetName);
           }}
         >
           {data.total}
         </TD>
         <TD className="text-center border border-gray-300">{data.average}</TD>
       </tr>
    </Fragment>
  );
};
